'use client'

import { useQuery } from '@apollo/client'
import { Container } from '@mui/material'
import { SelectGenre } from 'app/_components/SelectGenre'
import { useTrackVideoEvent } from 'app/_hooks/useTrackVideoEvent'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { gql } from '~/__generated__'
import { GetScreenPopupsDocument, GetScreenPopupsQuery } from '~/__generated__/graphql'
import { useHomeVideoList } from '~/features/home/hooks/useHomeVideoList'
import AdvertisementModal from '~/features/popup/components/AdvertisementModal'
import { KeyVisualModel } from '~/features/popup/hooks/createModel'
import { useKeyVisual } from '~/features/popup/hooks/useKeyVisual/useKeyVisual'
import { VideoPreviewModal } from '~/features/video/components/VideoPreviewModal'
import * as GA from '~/libs/ga'
import { HomePageContent } from './HomePageContent'

const GET_RECOMMENDATION_ROW_LIST_QUERY = gql(`
  query getRecommendationRowList($isPc: Boolean!, $timezone: String!) {
    recommendationRowList(isPc: $isPc, timezone: $timezone) {
      recommendationRows {
        id
        name
        rankable
        categoryId
      }
      recommendationSessionKey
    }
  }
`)

export const HomePageComponent = () => {
  const { push } = useRouter()
  const { data: recommendationRowListData } = useQuery(GET_RECOMMENDATION_ROW_LIST_QUERY, {
    variables: { isPc: !isMobile, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
  })
  const homeScreens = useHomeVideoList()
  const { keyVisualModels, attributionToken, loading: isKeyVisualsLoading } = useKeyVisual()
  const { trackVideoEvent } = useTrackVideoEvent()
  const {} = useQuery<GetScreenPopupsQuery>(GetScreenPopupsDocument) // 高速化のため、事前に取得しておく
  const [preview, setPreview] = useState<{ videoId: string; attributionToken?: string }>()

  const onKeyVisualShown = useCallback(
    (keyVisual: KeyVisualModel) => {
      trackVideoEvent({
        variables: { eventType: 'HOME_PAGE_VIEW', videoId: keyVisual.videoInfo.id },
      })
    },
    [trackVideoEvent],
  )

  const onClickDisplayAll = useCallback(
    (subCategoryId: number) => {
      GA.sendEvent({
        event: 'click',
        screen: 'stream',
        category: 'text',
        action: 'stream_all',
        label: `${subCategoryId}`,
      })
      void push(`/search/categories/${subCategoryId}/videos`)
    },
    [push],
  )

  const onClickVideoItem = useCallback(
    (videoId: number) => {
      GA.sendEvent({
        event: 'click',
        screen: 'stream',
        category: 'image',
        action: 'stream_poster',
        label: `${videoId}`,
      })
      setPreview({ videoId: videoId.toString() })
    },
    [push],
  )

  const onClickKeyVisual = useCallback(
    (keyVisual: KeyVisualModel) => {
      const video = keyVisual.videoInfo

      GA.sendEvent({
        event: 'click',
        screen: 'stream',
        category: 'image',
        action: 'stream_banner',
        label: `${video.id}`,
      })
      setPreview({ videoId: video.id, attributionToken })
    },
    [attributionToken],
  )

  return (
    <div data-testid='home-page-root'>
      <SelectGenre />
      <Container maxWidth='xl' disableGutters>
        <HomePageContent
          keyVisuals={keyVisualModels}
          homeScreens={homeScreens === 'loading' ? [] : homeScreens}
          recommendationRowList={recommendationRowListData?.recommendationRowList}
          onClickDisplayAll={onClickDisplayAll}
          onClickVideoItem={onClickVideoItem}
          onClickKeyVisual={onClickKeyVisual}
          onKeyVisualShown={onKeyVisualShown}
        />
        <AdvertisementModal />
      </Container>
      {preview && (
        <VideoPreviewModal
          videoId={preview.videoId}
          attributionToken={preview.attributionToken}
          onClose={() => setPreview(undefined)}
        />
      )}
    </div>
  )
}
